import { Link, createFileRoute } from "@tanstack/react-router";
import * as z from "zod/v4";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { DialogDescription } from "@radix-ui/react-dialog";
import ky from "ky";
import type { HTTPError } from "ky";
import { api, logout, saveTokens } from "@/lib/http";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/passwordInput";
import { Checkbox } from "@/components/ui/checkbox";
import {
  generateDefaultValue,
  requiredPassword,
  requiredString,
} from "@/lib/utils/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SubmitterText } from "@/components/ui/submit-button";
import { env } from "@/env";
import { PolygrafLogo } from "@/components/icons/logo";
import { AuthMarketingHeroCopy } from "@/routes/auth/-components/auth-marketing-hero-copy";
import { AuthMarketingPanel } from "@/routes/auth/-components/auth-marketing-panel";
import { cn } from "@/lib/utils";
import { parseBackendError, parseRefusal } from "@/lib/utils/backend";

export const Route = createFileRoute("/auth/login")({
  component: RouteComponent,
  staticData: {
    title: "Log In",
  },
});

const loginFieldClass =
  "h-auto rounded-lg border-[#d5d7da] bg-white px-3.5 py-2.5 text-base text-[#181d27] shadow-[0px_1px_2px_rgba(10,13,18,0.05)] placeholder:text-[#717680] focus-visible:border-[#dd2590] focus-visible:ring-[#dd2590]/25 dark:border-border dark:bg-input/30 dark:text-foreground dark:placeholder:text-muted-foreground";

const LoginFormSchema = z.object({
  email: requiredString("Email or username is required"),
  password: requiredPassword("Password is required"),
  rememberMe: z.boolean().optional(),
});
type LoginFormSchema = z.infer<typeof LoginFormSchema>;

type SigninResponse = { access_token: string } & (
  | {
      refresh_token: null;
      must_change_password: true;
    }
  | {
      refresh_token: string;
      must_change_password: false;
    }
);

function RouteComponent() {
  const navigate = Route.useNavigate();

  const [updateDialogPassword, setUpdateDialogPassword] = useState<
    string | null
  >(null);

  const form = useForm<LoginFormSchema>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const login = useMutation({
    mutationFn: async (json: Pick<LoginFormSchema, "email" | "password">) => {
      const res = await ky.post("api/v1/auth/login", {
        baseUrl: env.VITE_AUTH_API_URL,
        json,
      });
      return res.json<SigninResponse>();
    },
    onSuccess(data, vars) {
      saveTokens({ access: data.access_token, refresh: data.refresh_token });
      if (data.must_change_password) {
        setUpdateDialogPassword(vars.password);
      } else {
        navigate({ to: "/pricing" });
      }
    },
    onError(error: HTTPError) {
      const data = error.data;

      if (data && typeof data === "object" && "detail" in data) {
        if (typeof data.detail === "string") {
          form.setError("root.serverError", {
            message: data.detail,
          });
          return;
        } else {
          const refusal = parseRefusal(data.detail);
          if (refusal?.code === "EMAIL_NOT_VERIFIED") {
            form.setError("root.serverError", {
              message: "Please verify your email address before logging in.",
            });
            return;
          }
          if (refusal?.code === "ACCOUNT_SUSPENDED") {
            form.setError("root.serverError", {
              message: "Your account is suspended. Contact support.",
            });
            return;
          }
          const berrors = parseBackendError(data.detail);
          if (berrors !== null) {
            berrors.forEach(([loc, message]) =>
              // @ts-expect-error it is going to be awkward correctly casting the type, and we don't care about the wrong fields because they never get displayed
              form.setError(loc, { message }),
            );
          }
          return;
        }
      }
      form.setError("root.serverError", {
        message: "An unexpected error occurred",
      });
    },
  });

  return (
    <>
      <PasswordUpdateFormDialog
        password={updateDialogPassword}
        onSubmit={() => {
          setUpdateDialogPassword(null);
          form.resetField("password");
          logout();
        }}
      />
      <div className="flex h-full w-full justify-center overflow-y-auto bg-white font-[Manrope,system-ui,sans-serif] lg:overflow-hidden dark:bg-background">
        <div
          className={cn(
            "grid w-full max-w-[1440px] grid-cols-1 lg:h-full lg:min-h-0 lg:grid-cols-[minmax(0,560fr)_minmax(0,880fr)] lg:overflow-hidden",
          )}
        >
          <div className="flex w-full min-w-0 flex-col bg-white px-4 py-6 sm:px-6 sm:py-8 lg:h-full lg:min-h-0 lg:justify-between lg:overflow-y-auto lg:px-8 lg:pt-10 lg:pb-0 dark:bg-background">
            <div className="mb-8 flex w-full max-w-[360px] flex-col items-center self-center sm:mb-10 lg:mb-0 lg:items-start lg:self-start">
              <PolygrafLogo className="h-8 w-auto shrink-0 [&_path[data-slot=name]]:fill-[#181d27] dark:[&_path[data-slot=name]]:fill-white" />
            </div>

            <div className="flex w-full flex-col items-center gap-8 sm:gap-10 lg:flex-1 lg:items-start lg:justify-center">
              <AuthMarketingHeroCopy className="w-full max-w-[560px] lg:hidden" />

              <div className="flex w-full max-w-[360px] flex-col gap-8 sm:gap-10">
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-3 text-center lg:text-left">
                    <h1 className="text-3xl leading-9 font-semibold text-[#181d27] sm:text-4xl sm:leading-11 dark:text-foreground">
                      Log In
                    </h1>
                    <p className="text-base leading-6 text-[#535862] dark:text-muted-foreground">
                      Welcome back! Please enter your details.
                    </p>
                  </div>

                  <Form {...form}>
                    <form
                      className="flex flex-col gap-6"
                      onSubmit={form.handleSubmit((formData) => {
                        if (login.isPending) return;
                        login.mutate({
                          email: formData.email,
                          password: formData.password,
                        });
                      })}
                    >
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="gap-1.5">
                            <FormLabel className="text-sm font-medium text-[#414651] dark:text-foreground">
                              Email
                            </FormLabel>
                            <FormControl>
                              <Input
                                autoComplete="email"
                                placeholder="Enter your email"
                                className={loginFieldClass}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem className="gap-1.5">
                            <FormLabel className="text-sm font-medium text-[#414651] dark:text-foreground">
                              Password
                            </FormLabel>
                            <FormControl>
                              <PasswordInput
                                autoComplete="current-password"
                                placeholder="Enter your password"
                                className={loginFieldClass}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                        <FormField
                          control={form.control}
                          name="rememberMe"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center gap-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  className="cursor-pointer rounded border-[#d5d7da] data-[state=checked]:border-[#dd2590] data-[state=checked]:bg-[#dd2590] dark:border-border"
                                />
                              </FormControl>
                              <FormLabel className="cursor-pointer text-sm font-medium text-[#414651] dark:text-foreground">
                                Remember me
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                        <Link
                          className="shrink-0 cursor-pointer text-sm font-medium text-[#c11574] hover:underline dark:text-[#F472D0]"
                          to="/auth/forgot-password"
                        >
                          Forgot password?
                        </Link>
                      </div>

                      <Button
                        type="submit"
                        disabled={login.isPending}
                        className="relative h-auto w-full rounded-lg bg-[#dd2590] px-4 py-2.5 text-base font-medium text-white hover:bg-[#c11574] hover:text-white disabled:opacity-60"
                      >
                        <SubmitterText
                          submitting={login.isPending}
                          submittingChildren="Logging In..."
                        >
                          Log In
                        </SubmitterText>
                      </Button>

                      <p className="text-center text-sm font-medium text-[#414651] dark:text-foreground">
                        Don&apos;t have an account?{" "}
                        <Link
                          to="/auth/signup"
                          className="cursor-pointer text-[#dd2590] hover:underline"
                        >
                          Sign up
                        </Link>
                      </p>

                      {form.formState.errors.root?.serverError ? (
                        <div className="text-center text-sm text-destructive">
                          <p>
                            {form.formState.errors.root.serverError.message}
                          </p>
                          {form.formState.errors.root.serverError.message ===
                          "Please verify your email address before logging in." ? (
                            <Link
                              to="/auth/verify-code"
                              search={{ email: form.getValues("email") }}
                              className="mt-1 inline-block text-[#c11574] hover:underline"
                            >
                              Enter your verification code
                            </Link>
                          ) : null}
                        </div>
                      ) : null}
                    </form>
                  </Form>
                </div>
              </div>
            </div>

            <footer className="hidden w-full pt-8 pb-8 lg:block">
              <p className="text-sm text-[#535862] dark:text-muted-foreground">
                © Polygraf Meeting Guard 2026
              </p>
            </footer>
          </div>

          <AuthMarketingPanel />

          <footer className="w-full px-4 pt-2 pb-8 text-center sm:px-6 lg:hidden">
            <p className="mx-auto w-full max-w-[360px] text-sm text-[#535862] dark:text-muted-foreground">
              © Polygraf Meeting Guard 2026
            </p>
          </footer>
        </div>
      </div>
    </>
  );
}

const resetPasswordSchema = z
  .object({
    current: requiredPassword("Please enter current password"),
    new: requiredPassword("Please enter new password"),
    confirm: requiredPassword("Please enter new password again"),
  })
  .refine((data) => data.new === data.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

function PasswordUpdateFormDialog({
  password,
  onSubmit,
}: {
  password: string | null;
  onSubmit: () => void;
}) {
  const form = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      ...generateDefaultValue(resetPasswordSchema),
    },
  });
  useEffect(() => {
    if (password) {
      form.reset({
        ...generateDefaultValue(resetPasswordSchema),
        current: password,
      });
      new Promise((r) => setTimeout(r, 5)).then(() => form.setFocus("new"));
    }
  }, [password]);

  const passwordChangeMutation = useMutation({
    mutationFn: api.auth.changePassword,
    onSuccess() {
      toast.success(
        "Password changed successfuly. Please sign in with your new password",
      );

      onSubmit();
    },
  });
  return (
    <Dialog open={!!password}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Update Password</DialogTitle>
          <DialogDescription>
            The temporary password has expired, please create a permanent
            password
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((d) =>
              passwordChangeMutation.mutate(d),
            )}
            className="w-5/10 space-y-4"
          >
            <FormField
              control={form.control}
              name="current"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter current password"
                      autoComplete="current-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="new"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm new password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter new password again"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit">Submit</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
